import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, X } from 'lucide-react';

interface Props {
  text: string;
}

const DESKTOP_QUERY = '(min-width: 768px)';

export function HelpTooltip({ text }: Props) {
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_QUERY).matches : false,
  );
  const [position, setPosition] = useState({ left: 0, top: 0, above: true });

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const updateMode = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mediaQuery.addEventListener('change', updateMode);
    return () => mediaQuery.removeEventListener('change', updateMode);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  useEffect(() => {
    if (!open || isDesktop) return;
    mobileDialogRef.current?.focus();
  }, [isDesktop, open]);

  useEffect(() => {
    if (!open) return;

    const placeTooltip = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (isDesktop) {
        setPosition({ left: rect.left + rect.width / 2, top: rect.top - 8, above: true });
        return;
      }

      const margin = 12;
      const above = rect.top > 150;
      setPosition({
        left: margin,
        top: above ? rect.top - 8 : rect.bottom + 8,
        above,
      });
    };

    placeTooltip();
    window.addEventListener('resize', placeTooltip);
    window.addEventListener('scroll', placeTooltip, true);
    return () => {
      window.removeEventListener('resize', placeTooltip);
      window.removeEventListener('scroll', placeTooltip, true);
    };
  }, [isDesktop, open]);

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={() => isDesktop && setOpen(true)}
      onMouseLeave={() => isDesktop && setOpen(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label="Mostrar ayuda del campo"
        aria-describedby={open && isDesktop ? id : undefined}
        aria-controls={open && !isDesktop ? id : undefined}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onFocus={() => isDesktop && setOpen(true)}
        onBlur={() => isDesktop && setOpen(false)}
        className="-m-3.5 flex h-11 w-11 items-center justify-center rounded-full text-ui-text-subtle transition-colors hover:text-ui-text focus:outline-none md:m-0 md:h-auto md:w-auto md:focus-visible:ring-2 md:focus-visible:ring-ui-accent/40"
      >
        <CircleHelp size={16} aria-hidden="true" />
      </button>

      {open && isDesktop && createPortal(
        <span
          id={id}
          role="tooltip"
          style={{ left: position.left, top: position.top }}
          className="pointer-events-none fixed z-[70] w-64 -translate-x-1/2 -translate-y-full rounded-md border border-ui-border-strong bg-ui-surface-2 px-3 py-2 text-left text-xs font-normal leading-relaxed text-ui-text shadow-xl"
        >
          {text}
        </span>,
        document.body,
      )}

      {open && !isDesktop && createPortal(
        <div className="fixed inset-0 z-[70]" role="presentation">
          <button
            type="button"
            aria-label="Cerrar ayuda"
            className="absolute inset-0 cursor-default bg-transparent"
            onClick={close}
          />
          <div
            ref={mobileDialogRef}
            id={id}
            role="dialog"
            aria-label="Ayuda del campo"
            tabIndex={-1}
            style={{ left: position.left, top: position.top }}
            className={`fixed w-[calc(100vw-1.5rem)] rounded-lg border border-ui-border-strong bg-ui-surface-2 p-4 text-left shadow-2xl focus:outline-none ${position.above ? '-translate-y-full' : ''}`}
          >
            <div className="flex items-start gap-3">
              <CircleHelp className="mt-0.5 shrink-0 text-ui-accent" size={18} aria-hidden="true" />
              <p className="flex-1 text-sm font-normal leading-relaxed text-ui-text">{text}</p>
              <button
                type="button"
                aria-label="Cerrar ayuda"
                onClick={close}
                className="-m-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ui-text-muted transition-colors hover:bg-white/5 hover:text-ui-text focus:outline-none focus:ring-2 focus:ring-ui-accent/40"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
